import { examMetadataModule } from '@/store/examMetadata/module'
import { runCloudFunction } from '@/store/parseUtils'
import { callApi } from '@/store/apiUtils'
import { fetchLoadable, resetLoadable } from '@/store/utils'
import { keywordsModule } from '@/store/keywords/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import type { API } from '@pocketprep/types'

type ParseFetchKeywordDefinitions = (params: {
    serials: string[]
    examGuid: string
    examMetadataId?: string
}) => Promise<{
    keyword: string
    definition: string
    serial: string
}[]>

/** Maps keyword definitions by serial and keyword */
const mapKeywordDefinitions = (keywords: { keyword: string; definition: string; serial: string }[]) => {
    return keywords.reduce((acc, kd) => {
        if (!acc.has(kd.serial)) {
            acc.set(kd.serial, new Map())
        }

        acc.get(kd.serial)?.set(kd.keyword, kd.definition)
        return acc
    }, new Map<string, Map<string, string>>())
}

/**
 * The nest keyword-definitions endpoint caps how many serials it accepts per
 * request (FetchKeywordDefinitionsDto @ArrayMaxSize). A larger array is rejected
 * with a 400, so callers must batch. Keep this in sync with the server bound.
 */
const MAX_SERIALS_PER_REQUEST = 500

/** Split an array into contiguous batches of at most `size` items. */
const chunkArray = <T>(items: T[], size: number): T[][] => {
    const batches: T[][] = []
    for (let i = 0; i < items.length; i += size) {
        batches.push(items.slice(i, i + size))
    }
    return batches
}

const fetchKeywordDefinitions = async (serials: string[], forceFetch?: boolean, examGuid?: string) => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])

    const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const currentExamGuid = currentExamMetadata?.examGuid || examGuid
    const disabledKeywordExamGuids = import.meta.env.VUE_APP_DISABLED_KEYWORD_EXAMGUIDS
    if (!currentExamGuid || !currentExamMetadata?.objectId || disabledKeywordExamGuids?.includes(currentExamGuid)) {
        return
    }

    // Callers pass current-quiz serials plus every answered serial, which overlap,
    // so dedupe before counting toward the endpoint's per-request cap.
    const uniqueSerials = [ ...new Set(serials) ]

    // Nothing to look up. The endpoint rejects an empty array (@ArrayMinSize), so
    // returning here avoids a guaranteed 400 as well as a pointless round trip.
    if (!uniqueSerials.length) {
        return
    }

    // if keywords are already fetched for serials, we'll just return them, but otherwise we need
    // to clear out store and fetch again
    const currentKeywords = keywordsModule.getters.getKeywordDefinitions()
    const missingKeywords = uniqueSerials.filter(k => !currentKeywords.has(k))

    if (missingKeywords.length) {
        resetLoadable(keywordsModule.state.keywordDefinitions)
    }

    return fetchLoadable(keywordsModule.state.keywordDefinitions, async () => {
        const nestApiDomain = import.meta.env.VUE_APP_NEST_API_URL_DOMAIN
        if (!nestApiDomain) {
            const keywords = await runCloudFunction<ParseFetchKeywordDefinitions>('fetchKeywordDefinitions', {
                examGuid: currentExamGuid,
                serials: uniqueSerials,
                examMetadataId: currentExamMetadata?.objectId,
            })

            return mapKeywordDefinitions(keywords)
        }

        // Batch to the endpoint's per-request cap; a single oversized array is
        // rejected with a 400 (@ArrayMaxSize). Batches run concurrently and merge.
        const responses = await Promise.all(
            chunkArray(uniqueSerials, MAX_SERIALS_PER_REQUEST).map(
                (batch): Promise<API.SearchKeywordDefinitionsResponse> => callApi({
                    method: 'POST',
                    data: { serials: batch },
                    url: `/exams/${currentExamMetadata.objectId}/keyword-definitions/search`,
                })
            )
        )

        return mapKeywordDefinitions(responses.flatMap(response => response.keywords))
    }, forceFetch)
}

type SubmitKeywordDefinitionVote = (params: {
    keyword: string
    examGuid: string
    serial: string
    vote: 'helpful' | 'unhelpful'
    helpfulVoteChange: number
    unhelpfulVoteChange: number
}) => Promise<void>
const submitKeywordDefinitionVote =
    async (params: {
        keyword: string
        vote: 'helpful' | 'unhelpful'
        serial: string
        helpfulVoteChange: number
        unhelpfulVoteChange: number
    }) => {
        const currentExamMetadata = examMetadataModule.getters.getCurrentExamMetadata()
        if (!currentExamMetadata) {
            return
        }

        return runCloudFunction<SubmitKeywordDefinitionVote>('submitKeywordDefinitionVote', {
            examGuid: currentExamMetadata.examGuid,
            serial: params.serial,
            keyword: params.keyword,
            vote: params.vote,
            helpfulVoteChange: params.helpfulVoteChange,
            unhelpfulVoteChange: params.unhelpfulVoteChange,
        })
    }

export default {
    fetchKeywordDefinitions,
    submitKeywordDefinitionVote,
}
